import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { listChannels } from '../../api/channels';
import {
  importContactsCsv,
  importContactsJson,
  importContactsXlsx,
  previewContactsXlsx,
  type ColumnMapping,
  type ImportPreview,
} from '../../api/contacts';
import type { ImportResult } from '../../api/types';
import { Button } from '../ui/button';
import { Select } from '../ui/select';
import { useToast } from '../ui/toast';

export interface ImportModalProps {
  open: boolean;
  onClose: () => void;
}

type Format = 'csv' | 'json' | 'xlsx';

const MAPPING_FIELDS: { key: keyof ColumnMapping; label: string; required?: boolean }[] = [
  { key: 'vk_user_id', label: 'VK ID', required: true },
  { key: 'first_name', label: 'Имя' },
  { key: 'last_name', label: 'Фамилия' },
  { key: 'tags', label: 'Теги (через ;)' },
  { key: 'source', label: 'Источник' },
  { key: 'segment', label: 'Сегмент' },
];

export function ImportModal({ open, onClose }: ImportModalProps) {
  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels, enabled: open });
  const queryClient = useQueryClient();
  const toast = useToast();
  const [channelId, setChannelId] = useState('');
  const [format, setFormat] = useState<Format>('csv');
  const [raw, setRaw] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<keyof ColumnMapping, string>>>({});
  const [result, setResult] = useState<ImportResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  if (!open) return null;

  function resetFilePicked(f: File | null) {
    setFile(f);
    setPreview(null);
    setMapping({});
  }

  async function runPreview() {
    if (!channelId || !file) return;
    setPreviewing(true);
    try {
      const p = await previewContactsXlsx(channelId, file);
      setPreview(p);
      // Best-effort default mapping: match target field names to header text.
      const guess: Partial<Record<keyof ColumnMapping, string>> = {};
      for (const f of MAPPING_FIELDS) {
        const col = p.columns.find((c) => c.header.trim().toLowerCase().includes(f.key.split('_')[0] ?? ''));
        if (col) guess[f.key] = col.letter;
      }
      setMapping(guess);
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Не удалось прочитать файл');
    } finally {
      setPreviewing(false);
    }
  }

  async function submit() {
    if (!channelId) return;
    setSubmitting(true);
    setResult(null);
    try {
      let outcome: ImportResult;
      if (format === 'csv') {
        if (!raw.trim()) return;
        outcome = await importContactsCsv(channelId, raw);
      } else if (format === 'json') {
        if (!raw.trim()) return;
        outcome = await importContactsJson(channelId, JSON.parse(raw) as unknown[]);
      } else {
        if (!file || !mapping.vk_user_id) return;
        outcome = await importContactsXlsx(channelId, file, mapping as ColumnMapping);
      }
      setResult(outcome);
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Не удалось выполнить импорт');
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setRaw('');
    setResult(null);
    resetFilePicked(null);
    onClose();
  }

  const canSubmit =
    Boolean(channelId) &&
    (format === 'xlsx' ? Boolean(file && mapping.vk_user_id) : Boolean(raw.trim()));

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded bg-card p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Импорт контактов</h2>
          <button onClick={handleClose} aria-label="Закрыть" className="text-muted-fg hover:text-fg">
            ✕
          </button>
        </div>

        <div className="mb-3 flex gap-2">
          <Select aria-label="Канал" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
            <option value="">Выберите канал (vk_group)…</option>
            {(channels.data ?? [])
              .filter((c) => c.type === 'vk_group')
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
          </Select>
          <Select
            aria-label="Формат"
            value={format}
            onChange={(e) => {
              setFormat(e.target.value as Format);
              setResult(null);
            }}
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="xlsx">Excel (.xlsx)</option>
          </Select>
        </div>

        {format !== 'xlsx' && (
          <textarea
            className="h-48 w-full rounded border border-border bg-bg p-2 font-mono text-xs"
            placeholder={
              format === 'csv'
                ? 'vk_user_id,first_name,last_name\n123,Иван,Петров'
                : '[{"vk_user_id": 123, "first_name": "Иван"}]'
            }
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
          />
        )}

        {format === 'xlsx' && (
          <div className="space-y-3">
            <input
              aria-label="Файл .xlsx"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => resetFilePicked(e.target.files?.[0] ?? null)}
              className="block w-full text-xs"
            />
            <Button variant="secondary" size="sm" onClick={() => void runPreview()} disabled={!channelId || !file || previewing}>
              {previewing ? 'Читаю файл…' : 'Предпросмотр'}
            </Button>

            {preview && (
              <div className="space-y-2 rounded border border-border p-2">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        {preview.columns.map((c) => (
                          <th key={c.letter} className="border-b border-border px-1 py-1 text-left font-medium">
                            {c.letter}
                            {c.header && <div className="text-muted-fg">{c.header}</div>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.slice(0, 10).map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className="px-1 py-0.5">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-muted-fg">Всего строк: {preview.rowCount}</div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  {MAPPING_FIELDS.map((f) => (
                    <label key={f.key} className="text-xs">
                      <span className="mb-0.5 block text-muted-fg">
                        {f.label}
                        {f.required && ' *'}
                      </span>
                      <Select
                        aria-label={`Колонка: ${f.label}`}
                        value={mapping[f.key] ?? ''}
                        onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || undefined }))}
                      >
                        <option value="">Не сопоставлено</option>
                        {preview.columns.map((c) => (
                          <option key={c.letter} value={c.letter}>
                            {c.letter}
                            {c.header ? ` — ${c.header}` : ''}
                          </option>
                        ))}
                      </Select>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose}>
            Отмена
          </Button>
          <Button onClick={() => void submit()} disabled={submitting || !canSubmit}>
            {submitting ? 'Импортирую…' : 'Импортировать'}
          </Button>
        </div>

        {result && (
          <div className="mt-4 rounded border border-border p-3 text-sm">
            <div className="font-medium">Результат импорта ({result.status})</div>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-fg">
              <li>Всего строк: {result.total}</li>
              <li>Создано: {result.created}</li>
              <li>Дубликаты: {result.duplicates}</li>
              <li>Без разрешения: {result.denied}</li>
              {result.errors.length > 0 && <li>Ошибок разбора: {result.errors.length}</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
